import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CollapsibleLegHeader from '../../components/CollapsibleLegHeader'; // Import the new header component
import { useTheme } from '../../context/ThemeContext';
import clsx from 'clsx';
import SolidMapPin from '../../components/SolidMapPin';
import { useFormStore } from '../../stores/formData';
import OnBehalfButton from './OnBehalfButton';
import OnBehalfModal from './OnBehalfModal';
import useOnBehalf from '../../hooks/useOnBehalf';
import AddressFormV2 from './AddressForm2';

const ShippingLegAddress = ({ 
  legIndex,
  defaultExpanded = true,
  onClickBack,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { theme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showOnBehalfModal, setShowOnBehalfModal] = useState(false);

  const helpers = useFormStore((state) => state.helpers);
  const actions = useFormStore((state) => state.actions);
  const customerInfo = useFormStore((state) => state.formData.customerInfo);
  const formData = useFormStore((state) => state.formData);

  const { 
    onBehalfUser, 
    setOnBehalfUser, 
    clearOnBehalfUser 
  } = useOnBehalf();
  
  useEffect(() => {
    if (customerInfo && customerInfo?.authToken && customerInfo?.authToken.length > 0) {
      setIsAuthenticated(true);
      if (formData.customerInfo?.role?.search(/admin/i) !== -1 && !formData.onBehalfUser) { 
        setShowOnBehalfModal(true);
      }
      if(!formData.traveler) {
        actions.form.updateField('traveler', {
          name: `${customerInfo.firstName} ${customerInfo.lastName}`,
          email: customerInfo.email,
          phoneNumber: customerInfo.phoneNumber
        });
      }
    }
  }, [customerInfo]);

  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleUserSelected = useCallback((user) => {
    // Set the on-behalf-of user
    setOnBehalfUser(user);
    
    // Update form data store
    actions.form.updateField('onBehalfUser', {
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      phoneNumber: user.phone_number,
      authToken: user.auth_token
    });

    // Update traveler info to match selected user
    actions.form.updateField('traveler', {
      name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      phoneNumber: user.phone_number
    });
    
    // Close the modal
    setShowOnBehalfModal(false);
    
    // Force cart recreation with new user
    // actions.form.updateField('cart', { id: null });
  }, [actions, setOnBehalfUser]);

  const handleClearOnBehalfUser = useCallback(() => {
    clearOnBehalfUser();
    actions.form.updateField('onBehalfUser', null);
    
    // Force cart recreation with admin user
    // actions.form.updateField('cart', { id: null });
  }, [actions, clearOnBehalfUser]);


  const handleUpdateLegField = useCallback((legIndex, field, value) => {
    if(!isMountedRef.current) return;
    actions.leg.updateField(legIndex, field, value);
  }, [isMountedRef,actions]);

  const handleEditRequest = useCallback((legIndex, addressType) => {
    // Take user back to step 1 of the flow to edit address
    actions.leg.updateField(legIndex, 'activeAddressField', addressType);
    actions.form.updateField('activeLegIndex', legIndex);
    onClickBack?.()
  }, [actions, onClickBack]);

  const handleAddressChange = (legIndex, addressType, newAddress) => {
    handleUpdateLegField(legIndex, addressType, newAddress);
  };

  const removeLeg = useCallback(() => {
    actions.leg.removeLeg(legIndex);
  }, [legIndex, actions]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const totalLegPrice = useMemo(() => {
    return helpers.getLegTotal(legIndex);
  }, [helpers, legIndex]);

  // Get current leg data for display
  const currentLeg = useMemo(() => {
    return helpers.getLeg(legIndex);
  }, [helpers, legIndex]);

  // Theme-based styling
  const isDark = theme === 'dark';
  
  const containerClassName = clsx(
    'rounded-lg border transition-all duration-200',
    {
      'bg-slate-800 border-slate-700 hover:border-slate-600': isDark,
      'bg-white border-gray-200 hover:border-gray-300': !isDark
    }
  );

  const iconClassName = clsx(
    'text-lg',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const sectionLabelClassName = clsx(
    'text-xl font-semibold',
    {
      'text-slate-300': isDark,
      'text-gray-800': !isDark
    }
  );

  return (
    <>
      <div className="w-full flex justify-around py-3">
        {isAuthenticated && formData.onBehalfUser && formData.customerInfo?.role?.search(/admin/i) !== -1 && (
          <OnBehalfButton
            user={formData.onBehalfUser || onBehalfUser}
            onClick={() => setShowOnBehalfModal(true)}
            onCancel={handleClearOnBehalfUser}
          />
        )}
      </div>
      <div className={containerClassName}>
        {/* Collapsible Header */}
        <CollapsibleLegHeader
          legIndex={legIndex}
          isExpanded={isExpanded}
          onToggleExpanded={toggleExpanded}
          onRemove={removeLeg}
          showRemoveButton={false}
          totalPrice={totalLegPrice}
          fromAddress={currentLeg?.fromAddress}
          toAddress={currentLeg?.toAddress}
          shipDate={currentLeg?.shipDate}
          deliveryDate={currentLeg?.deliveryDate}
        />

        
        {/* Expandable Content */}
        {isExpanded && isAuthenticated && (
          <div className="animate-in fade-in duration-200">
            <div className="flex flex-col md:grid md:grid-cols-2 gap-6 p-2 md:p-6">
                <div className="md:border-r md:pr-6">
                  <div className="flex items-center gap-2 mb-3">
                    <SolidMapPin strokeWidth={4} size={18} className={iconClassName} />
                    <span className={sectionLabelClassName}>Origin</span>
                  </div>
                  <AddressFormV2 
                    legIndex={legIndex} 
                    addressType={'origin'}
                    fieldPrefix={`leg_${legIndex}_origin`}
                    initialAddress={currentLeg?.fromAddress}
                    onEditRequest={handleEditRequest}
                    onAddressChange={(address) => handleAddressChange(legIndex, 'fromAddress', address)}
                    showRemove={false}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <SolidMapPin strokeWidth={4} size={18} className={iconClassName} />
                    <span className={sectionLabelClassName}>Destination</span>
                  </div>
                  <AddressFormV2 
                    legIndex={legIndex} 
                    addressType={'destination'}
                    fieldPrefix={`leg_${legIndex}_destination`}
                    initialAddress={currentLeg?.toAddress}
                    onEditRequest={handleEditRequest}
                    onAddressChange={(address) => handleAddressChange(legIndex, 'toAddress', address)}
                    showRemove={false}
                  />
                </div>
              </div>
              {/* {currentLeg?.selectedProducts?.length && (
                <PackagingAndProtection
                  legIndex={legIndex}
                />
              )} */}
            </div>
          )}
          <OnBehalfModal
            isOpen={showOnBehalfModal}
            onClose={() => setShowOnBehalfModal(false)}
            onUserSelected={handleUserSelected}
            brandId={formData.brandId}
          />
        </div>
      </>
    );
};

ShippingLegAddress.propTypes = {
  legIndex: PropTypes.number.isRequired,
  defaultExpanded: PropTypes.bool,
  onClickBack: PropTypes.func,
};

export default ShippingLegAddress;